import { registerMockAPI } from '~/mocks/mockApis';

import { useQuery } from '@tanstack/react-query';

import { axiosInstance } from '~/services';

import { API_ENDPOINT } from '~/constants';

import { receiveKeys } from './keys';

import type { TypeErrorResponseData } from '~/types/common.types';
import type { TypeGetReturnDisposalCodesResponse, TypeReturnDisposalCodeApi } from '~/types/receive.types';

/** 렌탈사 코드 그룹 */
const RENTAL_COMPANY_CODE_GROUP = 'PALLET_RENTAL_COMPANY';

/** [입고관리 > 회송/폐기 관리] 렌탈사 코드 조회 API (정렬은 서버 응답에 위임) */
const fetchReturnDisposalRentalCompanies = async (): Promise<TypeReturnDisposalCodeApi[]> => {
  const { data } = await axiosInstance.get<TypeGetReturnDisposalCodesResponse>(
    API_ENDPOINT.RECEIVE.GET_RETURN_DISPOSAL_CODES,
    { params: { codeGroup: RENTAL_COMPANY_CODE_GROUP } }
  );

  return data.data.codes;
};

type TypeUseGetReturnDisposalRentalCompaniesQueryParams = {
  enabled?: boolean;
};

/** [입고관리 > 회송/폐기 관리] 렌탈사 코드 조회 Query Hook */
export const useGetReturnDisposalRentalCompaniesQuery = ({
  enabled = true,
}: TypeUseGetReturnDisposalRentalCompaniesQueryParams = {}) => {
  return useQuery({
    queryKey: receiveKeys.returnDisposalCodes(RENTAL_COMPANY_CODE_GROUP),
    queryFn: fetchReturnDisposalRentalCompanies,
    enabled,
    staleTime: 1000 * 60 * 5,
  });
};

/**
 * [입고관리 > 회송/폐기 관리] 코드 조회 Mock (렌탈사·택배사 공용 `GET /codes`)
 *
 * @note MSW 핸들러는 경로(`/codes`)만 매칭하고 codeGroup 쿼리를 구분하지 않으므로,
 *       렌탈사·택배사 응답을 하나의 시나리오 집합으로 묶어 devtools에서 전환한다.
 */
registerMockAPI<TypeGetReturnDisposalCodesResponse | TypeErrorResponseData>({
  method: 'get',
  endpoint: API_ENDPOINT.RECEIVE.GET_RETURN_DISPOSAL_CODES,
  label: '회송폐기관리/코드 조회(렌탈사·택배사)',
  scenarios: [
    {
      name: '렌탈사 정상 (KPP/AJU)',
      response: {
        data: {
          codes: [
            { codeId: 660, code: 'KPP', name: 'KPP', description: '파렛트 렌탈사 KPP', seqNo: 1 },
            { codeId: 661, code: 'AJU', name: '아주렌탈', description: '파렛트 렌탈사 아주렌탈', seqNo: 2 },
          ],
        },
      },
    },
    {
      name: '택배사 정상 (COURIER)',
      response: {
        data: {
          codes: [
            { codeId: 318, code: 'CJGLS', name: 'CJ대한통운', description: 'CJ대한통운', seqNo: 1 },
            { codeId: 319, code: 'HYUNDAI', name: '롯데택배', description: '롯데택배', seqNo: 2 },
            { codeId: 320, code: 'HANJIN', name: '한진택배', description: '한진택배', seqNo: 3 },
            { codeId: 321, code: 'KGB', name: '로젠택배', description: '로젠택배', seqNo: 4 },
            { codeId: 322, code: 'EPOST', name: '우체국택배', description: '우체국택배', seqNo: 5 },
          ],
        },
      },
    },
    {
      name: '서버 에러 (500)',
      response: { error: { code: 500, message: '코드 정보를 불러오지 못했습니다.' } },
      status: 500,
    },
  ],
});
